import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import LessonPlanChangeDiff from './LessonPlanChangeDiff';
import { sendChatMessage, applyChanges, calculateDiffStats, extractSectionContent } from '../utils/lessonPlanChatApi';
import { track } from '../utils/analytics';
import styles from './LessonPlanAIChat.module.css';

/**
 * AI Chat component for lesson plan editing
 * Allows teachers to chat with AI and make targeted edits to lesson plan sections
 */
function LessonPlanAIChat({ 
  lessonPlanId, 
  selectedSection, 
  onSectionDeselect, 
  lessonPlanContent,
  onChangesApplied,
  initialPrompt 
}) {
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suggestionHistory, setSuggestionHistory] = useState([]);
  const [lastApprovedChange, setLastApprovedChange] = useState(null);
  const [showUndoToast, setShowUndoToast] = useState(false);
  
  const messagesEndRef = useRef(null);
  const undoTimeoutRef = useRef(null);

  // When parent passes a prompt (e.g. from sample chips), populate the input
  useEffect(() => {
    if (initialPrompt && initialPrompt.text) {
      setInputMessage(initialPrompt.text);
    }
  }, [initialPrompt?.key]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Clear undo timeout on unmount
  useEffect(() => {
    return () => {
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }
    };
  }, []);

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || loading) return;

    const userMessage = inputMessage.trim();
    track('Clicked Send (Lesson Plan Chat)', { lesson_plan_id: lessonPlanId, screen_name: 'Display Lesson Plan', selected_section: selectedSection || null });
    setInputMessage('');
    setError(null);

    // Add user message to chat
    const newUserMessage = {
      role: 'user',
      content: userMessage,
      timestamp: new Date().toISOString()
    };
    setMessages(prev => [...prev, newUserMessage]);

    setLoading(true);

    try {
      // Send message to API
      const response = await sendChatMessage(
        lessonPlanId,
        userMessage,
        selectedSection,
        messages
      );

      // Strip out <PROPOSED_CHANGES> tags from the display (they're parsed separately)
      const cleanResponse = response.response.replace(
        /<PROPOSED_CHANGES[^>]*>[\s\S]*?<\/PROPOSED_CHANGES>/g, 
        ''
      ).trim();

      // If AI proposed changes, attach them to the message
      let proposedChanges = null;
      if (response.has_proposed_changes) {
        const oldContent = extractSectionContent(lessonPlanContent, response.section_affected);
        const diffStats = calculateDiffStats(oldContent || '', response.proposed_content);
        
        proposedChanges = {
          section: response.section_affected,
          content: response.proposed_content,
          originalContent: oldContent,
          linesAdded: diffStats.linesAdded,
          linesRemoved: diffStats.linesRemoved,
          action: response.action || 'update',
          newName: response.new_name || null,
          position: response.position || null,
          status: 'pending' // pending, applied, undone
        };
      }

      // Add AI response to chat (without the internal markup tags) with proposed changes attached
      const aiMessage = {
        role: 'assistant',
        content: cleanResponse,
        timestamp: new Date().toISOString(),
        proposedChanges: proposedChanges
      };
      setMessages(prev => [...prev, aiMessage]);
    } catch (err) {
      setError(err.message || 'Failed to send message');
      console.error('Chat error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApproveChanges = async (messageTimestamp) => {
    // Find the message with pending changes
    const messageIndex = messages.findIndex(msg => 
      msg.timestamp === messageTimestamp && msg.proposedChanges?.status === 'pending'
    );
    
    if (messageIndex === -1) return;
    
    const pendingChanges = messages[messageIndex].proposedChanges;
    
    setLoading(true);
    setError(null);

    try {
      const response = await applyChanges(
        lessonPlanId,
        pendingChanges.section,
        pendingChanges.content,
        pendingChanges.action || 'update',
        pendingChanges.newName || null,
        pendingChanges.position || null
      );

      // Save for undo functionality
      setLastApprovedChange({
        section: pendingChanges.section,
        previousContent: pendingChanges.originalContent,
        newContent: pendingChanges.content,
        messageTimestamp: messageTimestamp
      });

      // Update the message's proposed changes status to 'applied'
      setMessages(prev => {
        const updated = [...prev];
        updated[messageIndex] = {
          ...updated[messageIndex],
          proposedChanges: {
            ...updated[messageIndex].proposedChanges,
            status: 'applied'
          }
        };
        return updated;
      });

      // Notify parent component
      if (onChangesApplied) {
        onChangesApplied(response.updated_lesson_plan);
      }

      // Show undo toast
      setShowUndoToast(true);
      undoTimeoutRef.current = setTimeout(() => {
        setShowUndoToast(false);
        setLastApprovedChange(null);
      }, 10000); // 10 seconds

    } catch (err) {
      setError(err.message || 'Failed to apply changes');
      console.error('Apply changes error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleTryAgain = (messageTimestamp) => {
    // Find the message and mark changes as rejected
    const messageIndex = messages.findIndex(msg => msg.timestamp === messageTimestamp);
    if (messageIndex === -1) return;
    
    // Update message to rejected status
    setMessages(prev => {
      const updated = [...prev];
      if (updated[messageIndex].proposedChanges) {
        updated[messageIndex] = {
          ...updated[messageIndex],
          proposedChanges: {
            ...updated[messageIndex].proposedChanges,
            status: 'rejected'
          }
        };
      }
      return updated;
    });
    
    // Auto-send a message asking for variation
    const tryAgainMessage = "Please generate a different version of those changes.";
    setInputMessage(tryAgainMessage);
    setTimeout(() => handleSendMessage(), 100);
  };

  const handleRejectChanges = (messageTimestamp) => {
    // Find the message and mark changes as rejected
    const messageIndex = messages.findIndex(msg => msg.timestamp === messageTimestamp);
    if (messageIndex === -1) return;
    
    // Update message to rejected status
    setMessages(prev => {
      const updated = [...prev];
      if (updated[messageIndex].proposedChanges) {
        updated[messageIndex] = {
          ...updated[messageIndex],
          proposedChanges: {
            ...updated[messageIndex].proposedChanges,
            status: 'rejected'
          }
        };
      }
      return updated;
    });
  };

  const handleUndo = async () => {
    if (!lastApprovedChange) return;

    setLoading(true);
    setError(null);

    try {
      const response = await applyChanges(
        lessonPlanId,
        lastApprovedChange.section,
        lastApprovedChange.previousContent
      );

      // Clear undo state
      setShowUndoToast(false);
      
      // Update the message's proposed changes status to 'undone'
      setMessages(prev => {
        const messageIndex = prev.findIndex(msg => msg.timestamp === lastApprovedChange.messageTimestamp);
        if (messageIndex !== -1) {
          const updated = [...prev];
          updated[messageIndex] = {
            ...updated[messageIndex],
            proposedChanges: {
              ...updated[messageIndex].proposedChanges,
              status: 'undone'
            }
          };
          return updated;
        }
        return prev;
      });
      
      setLastApprovedChange(null);
      if (undoTimeoutRef.current) {
        clearTimeout(undoTimeoutRef.current);
      }

      // Notify parent component
      if (onChangesApplied) {
        onChangesApplied(response.updated_lesson_plan);
      }
    } catch (err) {
      setError(err.message || 'Failed to undo changes');
      console.error('Undo error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  return (
    <div className={styles.chatContainer}>
      {/* Section indicator */}
      {selectedSection && (
        <div className={styles.sectionIndicator}>
          <span className={styles.sectionLabel}>Editing:</span>
          <span className={styles.sectionName}>{selectedSection}</span>
          <button 
            className={styles.clearSection}
            onClick={onSectionDeselect}
            title="Clear selection"
          >
            ✕
          </button>
        </div>
      )}

      {/* Messages area */}
      <div className={styles.messagesArea}>
        {messages.length === 0 && (
          <div className={styles.emptyState}>
            <div className={styles.emptyIcon}>💬</div>
            <h3>AI Assistant</h3>
            <p>
              {selectedSection 
                ? `Ask me to help improve the "${selectedSection}" section.`
                : 'Ask me questions about your lesson plan or select a section to edit.'}
            </p>
          </div>
        )}

        {messages.map((msg, idx) => (
          <div 
            key={idx} 
            className={`${styles.message} ${styles[msg.role]}`}
          >
            {msg.role === 'user' && (
              <div className={styles.messageHeader}>
                <span className={styles.role}>You</span>
              </div>
            )}
            {msg.role === 'assistant' && (
              <div className={styles.messageHeader}>
                <span className={styles.role}>AI Assistant</span>
              </div>
            )}
            {msg.role === 'system' && (
              <div className={styles.systemMessage}>
                {msg.content}
              </div>
            )}
            {msg.role !== 'system' && (
              <div className={styles.messageContent}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {msg.content}
                </ReactMarkdown>
              </div>
            )}
            
            {/* Show proposed changes inline with the message */}
            {msg.proposedChanges && msg.proposedChanges.status !== 'rejected' && (
              <LessonPlanChangeDiff
                sectionName={msg.proposedChanges.section}
                oldContent={msg.proposedChanges.originalContent || ''}
                newContent={msg.proposedChanges.content}
                onApprove={msg.proposedChanges.status === 'pending' ? () => handleApproveChanges(msg.timestamp) : null}
                onTryAgain={msg.proposedChanges.status === 'pending' ? () => handleTryAgain(msg.timestamp) : null}
                onReject={msg.proposedChanges.status === 'pending' ? () => handleRejectChanges(msg.timestamp) : null}
                linesAdded={msg.proposedChanges.linesAdded}
                linesRemoved={msg.proposedChanges.linesRemoved}
                action={msg.proposedChanges.action}
                newName={msg.proposedChanges.newName}
                isApplied={msg.proposedChanges.status === 'applied' || msg.proposedChanges.status === 'undone'}
                appliedStatus={msg.proposedChanges.status}
                showCopyButton={true}
              />
            )}
          </div>
        ))}


        {loading && (
          <div className={styles.loadingIndicator}>
            <div className={styles.spinner}></div>
            <span>AI is thinking...</span>
          </div>
        )}

        {error && (
          <div className={styles.errorMessage}>
            <span className={styles.errorIcon}>⚠️</span>
            {error}
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Input area */}
      <div className={styles.inputArea}>
        <textarea
          className={styles.input}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder={selectedSection 
            ? `Ask about or edit the "${selectedSection}" section...` 
            : "Ask a question or request changes..."}
          rows={3}
          disabled={loading}
        />
        <button 
          className={styles.sendButton}
          onClick={handleSendMessage}
          disabled={loading || !inputMessage.trim()}
        >
          {loading ? '...' : 'Send'}
        </button>
      </div>

      {/* Undo toast */}
      {showUndoToast && (
        <div className={styles.undoToast}>
          <span>Changes applied successfully!</span>
          <div className={styles.toastActions}>
            <button 
              className={styles.undoButton}
              onClick={handleUndo}
            >
              ↺ Undo
            </button>
            <button 
              className={styles.closeToast}
              onClick={() => {
                setShowUndoToast(false);
                if (undoTimeoutRef.current) {
                  clearTimeout(undoTimeoutRef.current);
                }
              }}
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default LessonPlanAIChat;

